import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import type { Title } from '../../pages/SearchPage/useMentorsSearch'
import { MentorBase } from '../../types/mentor'
import { RootState } from '../store'

export interface MentorsState {
  selectedMentor: MentorBase | null
  isSearchingMentors: boolean
  isSearchingTitles: boolean
  favoriteMentors: string[]
  titles: Title[]
  featuredMentorsChunks: MentorBase[][] | null
  featuredMentorsList: MentorBase[]
  featuredMentorPage: number
}

const initialState: MentorsState = {
  selectedMentor: null,
  isSearchingMentors: false,
  isSearchingTitles: false,
  favoriteMentors: [],
  titles: new Array(3).fill({}) as Title[],
  featuredMentorsChunks: null,
  featuredMentorsList: [],
  featuredMentorPage: 0,
}

export const mentorsSlice = createSlice({
  name: 'mentors',
  initialState,
  reducers: {
    setSelectedMentor: (state, action: PayloadAction<MentorBase | null>) => {
      state.selectedMentor = action.payload
    },
    setIsSearchingMentors: (state, action: PayloadAction<boolean>) => {
      state.isSearchingMentors = action.payload
    },
    setIsSearchingTitles: (state, action: PayloadAction<boolean>) => {
      state.isSearchingTitles = action.payload
    },
    setFavoriteMentors: (state, action: PayloadAction<string[]>) => {
      state.favoriteMentors = action.payload
    },
    addToFavorites: (state, action: PayloadAction<string>) => {
      state.favoriteMentors = state.favoriteMentors.concat(action.payload)
    },
    removeFromFavorites: (state, action: PayloadAction<string>) => {
      state.favoriteMentors = state.favoriteMentors.filter(
        mentorId => mentorId !== action.payload,
      )
    },
    setTitles: (state, action: PayloadAction<Title[]>) => {
      state.titles = action.payload
    },
    setFeaturedMentorsChunks: (
      state,
      action: PayloadAction<MentorBase[][]>,
    ) => {
      state.featuredMentorsChunks = action.payload
    },
    setFeaturedMentors: (state, action: PayloadAction<MentorBase[]>) => {
      state.featuredMentorsList = [
        ...state.featuredMentorsList,
        ...action.payload,
      ]
    },
    setFeaturedMentorPage: (state, action: PayloadAction<number>) => {
      state.featuredMentorPage = action.payload
    },
  },
})

// Action creators are generated for each case reducer function
export const {
  setSelectedMentor,
  setIsSearchingMentors,
  setIsSearchingTitles,
  setFavoriteMentors,
  addToFavorites,
  removeFromFavorites,
  setTitles,
  setFeaturedMentorsChunks,
  setFeaturedMentors,
  setFeaturedMentorPage,
} = mentorsSlice.actions

export const getSelectedMentors = (state: RootState) =>
  state.mentorsReducer.selectedMentor

export const getIsSearchingMentors = (state: RootState) =>
  state.mentorsReducer.isSearchingMentors

export const getIsSearchingTitles = (state: RootState) =>
  state.mentorsReducer.isSearchingTitles

export const getFavoriteMentors = (state: RootState) =>
  state.mentorsReducer.favoriteMentors

export const getTitles = (state: RootState) => state.mentorsReducer.titles

export const getFeaturedMentorsChunks = (state: RootState) =>
  state.mentorsReducer.featuredMentorsChunks
export const getFeaturedMentors = (state: RootState) =>
  state.mentorsReducer.featuredMentorsList
export const getFeaturedMentorPage = (state: RootState) =>
  state.mentorsReducer.featuredMentorPage

export default mentorsSlice.reducer
